import React, { useEffect, useState } from "react";
import { X, Users } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { CandidateList } from "./CandidateList";

const MAX_INVITES = 19; // creator = 20th member

function useGroupForm(open, onCreated, onClose) {
  const [name, setName] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setSelected(new Set());
    api.get("/dms").then((r) => setCandidates(r.data || [])).catch(() => toast.error("Could not load travelers"));
  }, [open]);

  const toggle = (uid) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(uid)) n.delete(uid);
    else if (n.size < MAX_INVITES) n.add(uid);
    else toast.error("Groups are capped at 20 members");
    return n;
  });

  const submit = async () => {
    const cleaned = name.trim();
    if (!cleaned) { toast.error("Give your group a name"); return; }
    if (selected.size === 0) { toast.error("Pick at least one traveler"); return; }
    setSaving(true);
    try {
      const r = await api.post("/groups", { name: cleaned, member_ids: [...selected] });
      toast.success(`"${cleaned}" created ✨`);
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create group");
    } finally {
      setSaving(false);
    }
  };

  return { name, setName, candidates, selected, toggle, submit, saving, canSubmit: !!name.trim() && selected.size > 0 };
}

export default function CreateGroupModal({ open, onClose, onCreated }) {
  const f = useGroupForm(open, onCreated, onClose);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="create-group-modal">
      <div className="glass rounded-2xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button data-testid="create-group-close" onClick={onClose} className="absolute top-4 right-4 text-[#A89C98] hover:text-white" aria-label="Close"><X size={18} /></button>
        <div className="flex items-center gap-2 mb-4">
          <Users size={16} className="text-[#C8A96E]" />
          <h2 className="font-heading text-2xl">New Group</h2>
        </div>
        <label className="text-[10px] uppercase tracking-[0.25em] text-[#A89C98]">Group Name</label>
        <Input data-testid="create-group-name" value={f.name} onChange={(e) => f.setName(e.target.value)} placeholder="Wanderers of Bali…" maxLength={60} className="mt-1 bg-transparent border-b border-[#A89C98]/30 rounded-none h-10 text-white" />
        <div className="mt-5">
          <div className="flex items-baseline justify-between mb-2">
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#A89C98]">Invite Travelers</label>
            <span className="text-[10px] text-[#A89C98]" data-testid="create-group-count">{f.selected.size} selected · max {MAX_INVITES}</span>
          </div>
          <CandidateList candidates={f.candidates} selected={f.selected} onToggle={f.toggle} />
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <Button data-testid="create-group-cancel" onClick={onClose} className="bg-transparent border border-white/20 text-[#A89C98] hover:text-white hover:border-white/40 rounded-none h-10 px-4 uppercase tracking-widest text-xs">Cancel</Button>
          <Button data-testid="create-group-submit" onClick={f.submit} disabled={f.saving || !f.canSubmit} className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] rounded-none h-10 px-5 uppercase tracking-widest text-xs font-bold">
            {f.saving ? "Creating…" : "Create Group"}
          </Button>
        </div>
      </div>
    </div>
  );
}
